/**
 * ScoreDisplay Component
 *
 * Displays overall score with pass/fail badge and percentage.
 * Reusable for both trainer results view and admin pages.
 */

interface ScoreDisplayProps {
  /** Overall score (0-100) */
  overallScore: number;
  /** Maximum possible score */
  maxPossibleScore: number;
  /** Normalized score (0-1) */
  normalizedScore: number;
  /** Whether the assessment passed */
  passed: boolean;
  /** Optional size variant */
  size?: 'small' | 'medium' | 'large';
}

export function ScoreDisplay({
  overallScore,
  maxPossibleScore,
  normalizedScore,
  passed,
  size = 'medium',
}: ScoreDisplayProps) {
  const percentage = Math.round(normalizedScore * 100);

  const sizeStyles = {
    small: { fontSize: 14, badgeSize: 10 },
    medium: { fontSize: 18, badgeSize: 12 },
    large: { fontSize: 24, badgeSize: 14 },
  };

  const { fontSize, badgeSize } = sizeStyles[size];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      {/* Score Value */}
      <div
        style={{
          fontSize,
          fontWeight: 600,
          fontFeatureSettings: '"tnum"',
        }}
      >
        {overallScore.toFixed(2)} / {maxPossibleScore.toFixed(2)}
      </div>

      {/* Percentage */}
      <div
        style={{
          fontSize: badgeSize + 2,
          color: 'var(--text-secondary)',
        }}
      >
        ({percentage}%)
      </div>

      {/* Pass/Fail Badge */}
      <span
        style={{
          padding: `${badgeSize / 4}px ${badgeSize}px`,
          borderRadius: 4,
          fontSize: badgeSize,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          background: passed ? 'var(--semantic-success-light, #dcfce7)' : 'var(--semantic-error-light, #fee2e2)',
          color: passed ? 'var(--semantic-success-text, #166534)' : 'var(--semantic-error-text, #991b1b)',
          border: `1px solid ${passed ? 'var(--semantic-success-border, #86efac)' : 'var(--semantic-error-border, #fca5a5)'}`,
        }}
      >
        {passed ? 'Passed' : 'Failed'}
      </span>
    </div>
  );
}
